import { createContext, useContext, useState, ReactNode, useEffect, useMemo, useCallback } from 'react';
import { Product } from '@/data/products';
import { toast } from 'sonner';

const CART_STORAGE_KEY = 'solr_cart';
const CART_EXPIRY_DAYS = 7;

export interface CartItem {
  product: Product;
  quantity: number;
  selectedPackSize: number;
  isSubscription: boolean;
  selectedFlavor?: string;
  addedAt?: number;
}

interface CartContextType {
  items: CartItem[];
  addToCart: (product: Product, selectedPackSize?: number, isSubscription?: boolean, selectedFlavor?: string) => void;
  removeFromCart: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  getCartTotal: () => number;
  getCartCount: () => number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Load cart from localStorage
const loadCartFromStorage = (): CartItem[] => {
  try {
    const stored = localStorage.getItem(CART_STORAGE_KEY);
    if (!stored) return [];
    
    const cart: CartItem[] = JSON.parse(stored);
    const now = Date.now();
    const expiryTime = CART_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
    
    // Filter out expired items
    return cart.filter(item => {
      if (!item.addedAt) return true;
      return (now - item.addedAt) < expiryTime;
    });
  } catch (error) {
    console.error('Failed to load cart:', error);
    return [];
  }
};

// Save cart to localStorage
const saveCartToStorage = (cart: CartItem[]) => {
  try {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(cart));
  } catch (error) {
    console.error('Failed to save cart:', error);
  }
};

// Debounce utility
function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: ReturnType<typeof setTimeout>;
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export const CartProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>(() => loadCartFromStorage());

  // Debounced cart save
  const debouncedSaveCart = useMemo(
    () => debounce((cartItems: CartItem[]) => {
      saveCartToStorage(cartItems);
    }, 500),
    []
  );

  useEffect(() => {
    debouncedSaveCart(items);
  }, [items, debouncedSaveCart]);

  const addToCart = (product: Product, selectedPackSize = 12, isSubscription = false, selectedFlavor?: string) => {
    setItems((prevItems) => {
      const existingItem = prevItems.find((item) => 
        item.product.id === product.id && 
        item.selectedPackSize === selectedPackSize && 
        item.isSubscription === isSubscription &&
        item.selectedFlavor === selectedFlavor
      );
      
      if (existingItem) {
        // Increment quantity if item already exists with same options
        toast.success(`Added another ${product.name} (${selectedPackSize}-pack${isSubscription ? ' subscription' : ''}) to your cart`);
        return prevItems.map((item) =>
          item.product.id === product.id && 
          item.selectedPackSize === selectedPackSize && 
          item.isSubscription === isSubscription &&
          item.selectedFlavor === selectedFlavor
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      } else {
        // Add new item with quantity 1
        const packText = `${selectedPackSize}-pack${isSubscription ? ' subscription' : ''}`;
        toast.success(`Added ${product.name} (${packText}) to your cart`);
        return [...prevItems, { 
          product, 
          quantity: 1, 
          selectedPackSize, 
          isSubscription, 
          selectedFlavor,
          addedAt: Date.now(),
        }];
      }
    });
  };

  const removeFromCart = (productId: string) => {
    setItems((prevItems) => {
      const newItems = prevItems.filter((item) => item.product.id !== productId);
      return newItems;
    });
  };

  const updateQuantity = (productId: string, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(productId);
      return;
    }
    
    setItems((prevItems) =>
      prevItems.map((item) =>
        item.product.id === productId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setItems([]);
  };

  // Memoized calculations
  const getCartTotal = useCallback(() => {
    return items.reduce((total, item) => {
      const packSize = item.product.packSizes.find(pack => pack.size === item.selectedPackSize);
      const price = item.isSubscription ? (packSize?.subscription_price || packSize?.price || 0) : (packSize?.price || 0);
      return total + price * item.quantity;
    }, 0);
  }, [items]);

  const getCartCount = useCallback(() => {
    return items.reduce((count, item) => count + item.quantity, 0);
  }, [items]);

  return (
    <CartContext.Provider
      value={{
        items,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        getCartTotal,
        getCartCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
